//@ts-nocheck
import request from '../request';
import globalConfig from '../../config/globalConfig';

const DRAFT_PATH = 'trip-import/draft';

/**
 * Builds the absolute URL for the draft SSE endpoint, joining the configured
 * API base URL and the path without ever producing a double slash (the local
 * `VITE_API_URL` ships with a trailing slash, e.g. `.../v1/`).
 *
 * @returns {string}
 */
const buildDraftUrl = () => {
  const baseUrl = String(globalConfig.axios.apiUrl || '').replace(/\/+$/, '');
  return `${baseUrl}/${DRAFT_PATH}`;
};

/**
 * Clears the session and redirects to /login, mirroring the 401 handling in
 * services/request.js. Not called automatically for the import flow: an
 * expired session mid-import surfaces as a `SESSION_EXPIRED` error so the UI
 * can explain what happened first; the "Log in" button calls this.
 */
export const clearSessionAndGoToLogin = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('userSession');

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/**
 * Builds the Error to throw for a non-OK draft response.
 * @param {Response} response
 * @returns {Promise<Error>}
 */
const buildErrorFromResponse = async (response) => {
  if (response.status === 401) {
    const error = new Error('Session expired');
    error.code = 'SESSION_EXPIRED';
    return error;
  }

  if (response.status === 404) {
    const error = new Error('Trip import is not available');
    error.code = 'FEATURE_DISABLED';
    return error;
  }

  if (response.status === 400) {
    let code = 'INTERNAL';
    try {
      const body = await response.json();
      code = body?.code || body?.error?.code || code;
    } catch (err) {
      // Response body wasn't JSON; fall back to INTERNAL below.
    }
    const error = new Error('Trip import request was invalid');
    error.code = code;
    return error;
  }

  const error = new Error(`Trip import request failed (${response.status})`);
  error.code = 'INTERNAL';
  return error;
};

/**
 * Creates a pure, chunk-agnostic SSE line parser. Feed it every decoded text
 * chunk (in order) via the returned function; every complete `data: {...}`
 * frame is JSON-parsed and handed to `onData`. Frames split across chunk
 * boundaries are buffered until complete. `data: [DONE]` and blank lines are
 * consumed silently.
 *
 * @param {(event: Object) => void} onData
 * @returns {(chunkText: string) => void}
 */
export const createSseParser = (onData) => {
  let buffer = '';

  return (chunkText = '') => {
    buffer += chunkText;
    const lines = buffer.split('\n');
    // The last entry may be an incomplete line; keep it buffered.
    buffer = lines.pop() ?? '';

    lines.forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line.startsWith('data:')) {
        return;
      }

      const data = line.slice('data:'.length).trim();
      if (!data || data === '[DONE]') {
        return;
      }

      try {
        onData(JSON.parse(data));
      } catch (err) {
        // Skip invalid JSON / partial SSE fragments.
      }
    });
  };
};

/**
 * Streams a trip import draft from operator-typed fields plus free text, a
 * link and/or an attached file. Calls `onEvent` for every `progress` /
 * `draft` / `error` SSE event the server sends. An aborted `signal` rejects
 * the returned promise with the fetch's native `AbortError`.
 *
 * @param {Object} input
 * @param {Object} [input.fields] - Operator-typed fields (title, startDate, duration, destination).
 * @param {string} [input.text] - Free text or a pasted itinerary.
 * @param {string} [input.url] - A link to read.
 * @param {File} [input.file] - A PDF or DOCX file.
 * @param {Object} [options]
 * @param {(event: Object) => void} [options.onEvent] - Called for every parsed SSE event.
 * @param {AbortSignal} [options.signal] - Aborts the stream.
 * @returns {Promise<void>}
 */
export const streamTripImportDraft = async (
  { fields, text, url, file } = {},
  { onEvent, signal } = {}
) => {
  const payload = {};
  if (fields && Object.keys(fields).length > 0) {
    payload.fields = fields;
  }
  if (text && text.trim()) {
    payload.text = text;
  }
  if (url && url.trim()) {
    payload.url = url;
  }

  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));
  if (file) {
    formData.append('file', file, file.name);
  }

  const accessToken = localStorage.getItem('accessToken');
  const headers = {
    'accept-language': 'en'
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(buildDraftUrl(), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
    signal
  });

  if (!response.ok) {
    throw await buildErrorFromResponse(response);
  }

  // The draft endpoint can, in principle, resolve without ever starting the
  // SSE stream (e.g. a validation error returned as plain JSON with a 2xx
  // status by a proxy). Guard on content-type before trying to read it as a
  // stream, and map a `{code}` body the same way a 400 is mapped above.
  const contentType = response.headers?.get?.('content-type') || '';
  if (!contentType.includes('text/event-stream')) {
    let code = 'INTERNAL';
    try {
      const body = await response.json();
      code = body?.code || body?.error?.code || code;
    } catch (err) {
      // Body wasn't JSON either; fall back to INTERNAL below.
    }
    const error = new Error('Unexpected response from trip import');
    error.code = code;
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const feed = createSseParser((event) => onEvent?.(event));

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    feed(decoder.decode(value, { stream: true }));
  }
};

/**
 * Commits a confirmed trip import draft, creating the trip.
 *
 * @param {Object} draft - The `TripImportDraft` returned by the draft stream.
 * @returns {Promise<number|undefined>} The created trip id.
 */
export const commitTripImport = (draft) =>
  request
    // skipAuthRedirect: on a 401 we want to show a session-expired message
    // instead of the global silent redirect to /login (see request.js).
    .post(
      'trip-import/commit',
      { draft },
      { timeout: 180000, skipAuthRedirect: true }
    )
    // `warnings` are summary decisions for things that could not be done while creating the trip (e.g. images that
    // failed to save); the trip itself was created.
    .then((res) => ({
      tripId: res?.data?.tripId,
      warnings: Array.isArray(res?.data?.warnings) ? res.data.warnings : []
    }))
    .catch((err) => {
      if (err?.response?.status === 401) {
        const error = new Error('Session expired');
        error.code = 'SESSION_EXPIRED';
        throw error;
      }
      throw err;
    });

/**
 * The stored "what the import did" summary of a trip, so it can be reopened after the confirmation step is gone.
 * Resolves `null` for a trip that was not created by an import, and for any failure (the panel just stays hidden).
 *
 * @param {number|string} tripId
 * @returns {Promise<Object|null>}
 */
export const getTripImportSummary = (tripId) =>
  request
    .get(`trip-import/summary/${tripId}`)
    .then((res) => res?.data ?? null)
    .catch(() => null);
